/**
 * Illustration Assets
 *
 * General illustrations for empty states, onboarding, and other UI contexts.
 */

import type { AssetPath } from '../types';

/**
 * Base path for illustrations
 */
const ILLUSTRATIONS_BASE = '/assets/images/illustrations';

/**
 * Empty state illustrations
 */
export const EMPTY_STATE_ILLUSTRATIONS = {
  default: {
    path: `${ILLUSTRATIONS_BASE}/empty-state.svg`,
    alt: 'No data available',
    width: 240,
    height: 180,
  },
  search: {
    path: `${ILLUSTRATIONS_BASE}/empty-search.svg`,
    alt: 'No results found',
    width: 240,
    height: 180,
  },
  documents: {
    path: `${ILLUSTRATIONS_BASE}/empty-documents.svg`,
    alt: 'No documents yet',
    width: 240,
    height: 180,
  },
  tasks: {
    path: `${ILLUSTRATIONS_BASE}/empty-tasks.svg`,
    alt: 'All tasks completed',
    width: 240,
    height: 180,
  },
  notifications: {
    path: `${ILLUSTRATIONS_BASE}/empty-notifications.svg`,
    alt: 'No notifications',
    width: 240,
    height: 180,
  },
} as const;

/**
 * Onboarding illustrations
 */
export const ONBOARDING_ILLUSTRATIONS = {
  welcome: {
    path: `${ILLUSTRATIONS_BASE}/onboarding-welcome.svg`,
    alt: 'Welcome to Tonnie',
    width: 320,
    height: 240,
  },
  profile: {
    path: `${ILLUSTRATIONS_BASE}/onboarding-profile.svg`,
    alt: 'Complete your profile',
    width: 320,
    height: 240,
  },
  journey: {
    path: `${ILLUSTRATIONS_BASE}/onboarding-journey.svg`,
    alt: 'Start your journey',
    width: 320,
    height: 240,
  },
  linkedin: {
    path: `${ILLUSTRATIONS_BASE}/onboarding-linkedin.svg`,
    alt: 'Connect LinkedIn',
    width: 320,
    height: 240,
  },
} as const;

/**
 * Success/completion illustrations
 */
export const SUCCESS_ILLUSTRATIONS = {
  checkup: {
    path: `${ILLUSTRATIONS_BASE}/success-checkup.svg`,
    alt: 'Assessment completed',
    width: 280,
    height: 210,
  },
  journey: {
    path: `${ILLUSTRATIONS_BASE}/success-journey.svg`,
    alt: 'Journey created',
    width: 280,
    height: 210,
  },
  milestone: {
    path: `${ILLUSTRATIONS_BASE}/success-milestone.svg`,
    alt: 'Milestone achieved',
    width: 280,
    height: 210,
  },
} as const;

/**
 * Error illustrations
 */
export const ERROR_ILLUSTRATIONS = {
  notFound: {
    path: `${ILLUSTRATIONS_BASE}/error-404.svg`,
    alt: 'Page not found',
    width: 320,
    height: 240,
  },
  serverError: {
    path: `${ILLUSTRATIONS_BASE}/error-500.svg`,
    alt: 'Something went wrong',
    width: 320,
    height: 240,
  },
  offline: {
    path: `${ILLUSTRATIONS_BASE}/error-offline.svg`,
    alt: 'No internet connection',
    width: 320,
    height: 240,
  },
} as const;

/**
 * All illustrations
 */
export const ILLUSTRATIONS = {
  emptyState: EMPTY_STATE_ILLUSTRATIONS,
  onboarding: ONBOARDING_ILLUSTRATIONS,
  success: SUCCESS_ILLUSTRATIONS,
  error: ERROR_ILLUSTRATIONS,
} as const;

/**
 * Get illustration asset
 */
export function getIllustration(
  category: keyof typeof ILLUSTRATIONS,
  name: string
): AssetPath | undefined {
  const categoryIllustrations = ILLUSTRATIONS[category] as Record<string, AssetPath>;
  return categoryIllustrations[name];
}
